<template lang="pug">
.order-history-cave-item
  rogue-link.thumb(:to="linkPath" :productId="caveGroupProduct?.productId")
    StaticImage(:src="caveBundleRack?.thumbnail || null" :width="83" :alt="caveGroupProduct?.name || ''")
  .middle
    rogue-link.name(:to="linkPath" :productId="caveGroupProduct?.productId")
      h5 {{ caveGroupProduct?.name || '' }}
    .sku {{ caveGroupProduct?.sku }}
    .cave-rack-attr(v-for="[key, val] in rackAttributes" :key="key") {{ key }}: {{ val }}
  .cave-addon(v-if="caveBundleRack || caveBundleLeft || caveBundleRight")
    h5.included-items {{ __('Included Items') }}
    //- Rack Configuration
    .cave-section(v-if="caveBundleRack")
      .cave-section-label {{ __('Rack') }}
      .cave-section-items
        .cave-child-item(
          v-for="child in caveBundleRackChildren"
          :key="child.productId"
        )
          StaticImage.cave-child-thumb(
            :src="child.thumbnail || null"
            :width="65"
            :alt="child.name || ''"
          )
          .cave-child-name {{ child.name || '' }}
    //- Left Side
    .cave-section(v-if="caveBundleLeft")
      .cave-section-label {{ __('Left Side') }}
      .cave-section-items
        .cave-child-item(
          v-for="child in caveBundleLeftChildren"
          :key="child.productId"
        )
          StaticImage.cave-child-thumb(
            :src="child.thumbnail || null"
            :width="65"
            :alt="child.name || ''"
          )
          .cave-child-name {{ child.name || '' }}
    //- Right Side
    .cave-section(v-if="caveBundleRight")
      .cave-section-label {{ __('Right Side') }}
      .cave-section-items
        .cave-child-item(
          v-for="child in caveBundleRightChildren"
          :key="child.productId"
        )
          StaticImage.cave-child-thumb(
            :src="child.thumbnail || null"
            :width="65"
            :alt="child.name || ''"
          )
          .cave-child-name {{ child.name || '' }}
</template>
<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRogue } from '@/js/services/Rogue'
import { useProductStore } from '@/js/stores/productStore'
import { LEFT_CAVE_BUNDLE, MAIN_CAVE_BUNDLE, MAIN_CAVE_GROUP, RIGHT_CAVE_BUNDLE } from '@/js/pages/product/rackbuilder/products/cave/utils/caveConstants'
import type { BuyRequest } from '@/js/services/RogueTypes'

const props = defineProps<{
buyRequest: BuyRequest,
listItems: BuyRequest[],
}>()

const rogue        = useRogue()
const productStore = useProductStore()

const products = computed(() => productStore.products)

// We load the group product by SKU to get the canonical Cave name, product ID, SKU, and PDP url.
const caveGroupProduct = computed(() =>Object.values(products.value).find(p => p.sku === MAIN_CAVE_GROUP) ?? null)

const linkPath = computed(() => {
  const base = caveGroupProduct.value?.url
  const recipe = props.buyRequest.metadata?.recipe

  return base && recipe ? `${base}?rid=${recipe}` : base
})

// Recipe siblings — all three bundle items from the order sharing the same recipe ID
const recipeItems = computed(() =>
  props.listItems.filter(item => item.metadata?.recipe === props.buyRequest.metadata?.recipe)
)

const caveBundleRack  = computed(() => recipeItems.value.find(item => (item.sku || products.value[item.productId]?.sku) === MAIN_CAVE_BUNDLE) ?? null)
const caveBundleLeft  = computed(() => recipeItems.value.find(item => (item.sku || products.value[item.productId]?.sku) === LEFT_CAVE_BUNDLE) ?? null)
const caveBundleRight = computed(() => recipeItems.value.find(item => (item.sku || products.value[item.productId]?.sku) === RIGHT_CAVE_BUNDLE) ?? null)

// Removing the first chiild out of each bundle so they are not displayed
const caveBundleRackChildren  = computed(() => caveBundleRack.value?.children?.slice(1) ?? [])
const caveBundleLeftChildren  = computed(() => caveBundleLeft.value?.children?.slice(1) ?? [])
const caveBundleRightChildren = computed(() => caveBundleRight.value?.children?.slice(1) ?? [])

// Display rack attributes like color, height, rack depth, etc.
// Prepends rack type (e.g. "Fixed Rack" / "Fold Back Rack") from the product store since it's not in the configuration object.
const rackAttributes = computed(() => {
  const firstChild   = caveBundleRack.value?.children?.[0]
  const rackTypeName = firstChild ? (products.value[firstChild.productId]?.name ?? null) : null
  const configAttrs  = Object.entries((firstChild?.configuration ?? {}) as Record<string, string>)
    .map(([key, val]) => [key, typeof val === 'string' ? val.replace(/\\"/g, '"') : val])

  return rackTypeName ? [['Rack', rackTypeName], ...configAttrs] : configAttrs
})

// Loads the Cave group product (for display name, productID, and SKU) + all recipe siblings and their children
async function load() {
  await productStore.load(MAIN_CAVE_GROUP, rogue)

  const toLoad: number[] = [props.buyRequest.productId]

  props.listItems
    .filter(item => item.metadata?.recipe === props.buyRequest.metadata?.recipe)
    .forEach(item => {
      toLoad.push(item.productId)
      item.children?.forEach(child => {
        toLoad.push(child.productId)
        child.children?.forEach(grandchild => toLoad.push(grandchild.productId))
      })
    })

  await productStore.loadAll(toLoad.filter(id => id !== 0), rogue)
}

onMounted(() => { load() })
</script>
<style lang="scss" scoped>
$grid-left         : calc(65px + 16px);
$grid-left-desktop : calc(83px + 16px);

.order-history-cave-item {
  // flex-grow: 1 ensures this element fills the .product column in order-detail.vue's flex row.
  // The parent delegator (order-history-custom-item.vue) is a fragment so .product class
  // attr inheritance does not reach here — this compensates for that.
  flex-grow: 1;
  display: grid;
  max-width: 100%;
  align-items: start;
  word-wrap: break-word;
  margin-top: 10px;
  margin-bottom: 10px;

  grid-template-columns: $grid-left minmax(100px, 1fr);
  grid-template-areas:
    "thumb middle"
    "addon addon";

  @include all-desktop() {
    grid-template-columns: $grid-left-desktop minmax(100px, 1fr);
    grid-template-areas:
      "thumb middle"
      ". addon";
  }
}

.thumb {
  grid-area: thumb;
  width: 65px;
  display: block;

  @include all-desktop() {
    width: 83px;
  }
}

.middle {
  grid-area: middle;
  padding: 0;
}

.name {
  color: $color-black;
  text-decoration: none;

  h5 {
    margin-bottom: 0;
    white-space: pre-wrap;
  }
}

.sku {
  font-size: $size-xxs;
  margin-top: 5px;
  color: #666;
  margin-bottom: $space-xxxs;
}

.cave-rack-attr {
  font-size: $size-xxs;
  margin-top: $space-xs;
  line-height: 1em;
  color: $color-black;
}

.cave-addon {
  grid-area: addon;
  margin-top: $space-s;
}

.included-items {
  font-size: $size-xxs;
  text-transform: uppercase;
  padding-bottom: $space-xxs;
  border-bottom: 1px solid $color-gray-light;
  margin-bottom: $space-xs;
}

.cave-section {
  margin-bottom: $space-s;
}

.cave-section-label {
  font-size: $size-xxs;
  font-weight: $weight-bold;
  text-transform: uppercase;
  color: $color-black;
  margin-bottom: $space-xxs;
}

.cave-section-items {
  display: flex;
  flex-direction: column;
  gap: $space-xxs;
  margin-top: $space-xxs;
  padding-left: $space-s;
}

.cave-child-item {
  display: flex;
  align-items: center;
  gap: $space-xxs;
}

.cave-child-thumb {
  flex-shrink: 0;
  width: 65px;
}

.cave-child-name {
  font-size: $size-xxs;
  color: $color-black;
  line-height: $line-height-l;
}
</style>
